import * as React from "react";
import { Box, Paper, Stack, Tab } from "@mui/material";
import StandardLayout from "../../../layouts/standard";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import CclSavePreferencesButton from "../cclSavePreferencesButton/cclSavePreferencesButton";

/** @deprecated Use DetailPageTab object type instead */
export type DetailPageTabListType = [string, number, React.ReactNode, boolean?][];

export type DetailPageTab = {
    label: string;
    index: number;
    content: React.ReactNode;
    visible?: boolean;
    saveable?: boolean;
};

type NormalizedTab = Required<Pick<DetailPageTab, "label" | "index" | "content">> &
    Pick<DetailPageTab, "visible" | "saveable">;

function normalizeTabs(
    tablist: DetailPageTabListType | DetailPageTab[]
): NormalizedTab[] {
    if (tablist.length === 0) return [];
    if (Array.isArray(tablist[0]) || typeof tablist[0] !== "object") {
        return (tablist as DetailPageTabListType).map((t) => ({
            label: t[0],
            index: t[1],
            content: t[2],
            visible: t.length > 3 ? t[3] : undefined,
        }));
    }
    return tablist as DetailPageTab[];
}

export type DetailPageProps = {
    breadcrumbs: JSX.Element;
    descriptor: string;
    tablist: DetailPageTabListType | DetailPageTab[];
    activeTab?: number;
    identifier?: string;
    handleTabChange?: (event: React.SyntheticEvent, newValue: number) => void;
    showSavePreferences?: boolean;
    preferencesArea?: string;
    headerContent?: React.ReactNode;
};

const DetailPage: React.FC<DetailPageProps> = (props) => {
    const [value, setValue] = React.useState(props.activeTab != null ? `${props.activeTab}` : `0`);

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        if (props.handleTabChange) {
            props.handleTabChange(event, +newValue);
            return;
        }
        setValue(newValue);
    };

    const allTabs = normalizeTabs(props.tablist);
    const visibleTabs = allTabs.filter((tab) => tab.visible !== false);
    const activeTabObj = visibleTabs.find((tab) => `${tab.index}` === value);
    const showSaveIcon =
        props.showSavePreferences &&
        props.preferencesArea &&
        activeTabObj?.saveable === true;

    React.useEffect(() => {
        if (props.activeTab !== null && props.activeTab !== undefined) {
            setValue(`${props.activeTab}`);
        } else {
            setValue(`${visibleTabs[0]?.index}`);
        }
        // eslint-disable-next-line
    }, [props.activeTab]);

    function a11yProps(index: number) {
        return {
            id: `${props.descriptor}-detail-tab-${index}`,
            "aria-controls": `${props.descriptor}-detail-tabpanel-${index}`,
        };
    }

    return (
        <StandardLayout breadcrumbs={props.breadcrumbs}>
            <Stack height={1} width={1}>
                {props.headerContent}
                <Paper
                    id={"details-layout-" + props.identifier}
                    sx={{
                        width: 1,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "5px",
                    }}
                >
                    <TabContext value={`${value}`}>
                        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                            <TabList
                                variant="scrollable"
                                onChange={handleTabChange}
                                aria-label={`${props.descriptor}-detail-tabs`}
                            >
                                {visibleTabs.map((tab, i) => (
                                    <Tab
                                        sx={{ textTransform: "none" }}
                                        key={tab.label}
                                        label={tab.label}
                                        value={`${tab.index}`}
                                        {...a11yProps(i)}
                                    />
                                ))}
                            </TabList>
                        </Box>
                        {visibleTabs.map((tab, i) => (
                            <TabPanel key={tab.label} sx={{ height: "100%", position: "relative" }} value={`${tab.index}`}>
                                {showSaveIcon && (
                                    <Box sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}>
                                        <CclSavePreferencesButton area={props.preferencesArea!} />
                                    </Box>
                                )}
                                <Box sx={{ p: 1, height: "100%", display: "flex" }}>{tab.content}</Box>
                            </TabPanel>
                        ))}
                    </TabContext>
                </Paper>
            </Stack>
        </StandardLayout>
    );
};

export default DetailPage;
